import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Plus, Trash2, Tag, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { formatPrice } from '@/lib/utils';

export default function AdminCouponsPage() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    code: '', discountType: 'percentage', discountValue: '', maxUses: '', courseId: '', minPrice: '', expiresAt: '',
  });

  const { data: coupons, isLoading } = useQuery({ queryKey: ['admin-coupons'], queryFn: adminApi.getCoupons });
  const { data: courses } = useQuery({ queryKey: ['admin-courses'], queryFn: adminApi.getCourses });

  const createMutation = useMutation({
    mutationFn: adminApi.createCoupon,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-coupons'] });
      setShowForm(false);
      setForm({ code: '', discountType: 'percentage', discountValue: '', maxUses: '', courseId: '', minPrice: '', expiresAt: '' });
      toast({ title: 'Coupon created!' });
    },
    onError: (e: any) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => adminApi.updateCoupon(id, { isActive }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-coupons'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: adminApi.deleteCoupon,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-coupons'] });
      toast({ title: 'Coupon deleted' });
    },
  });

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.code || !form.discountValue) {
      toast({ title: 'Error', description: 'Code and discount value are required', variant: 'destructive' });
      return;
    }
    createMutation.mutate({
      code: form.code,
      discountType: form.discountType,
      discountValue: Number(form.discountValue),
      maxUses: form.maxUses ? Number(form.maxUses) : null,
      courseId: form.courseId || null,
      minPrice: form.minPrice ? Number(form.minPrice) : null,
      expiresAt: form.expiresAt || null,
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Coupons</h1>
          <p className="text-muted-foreground">Manage discount codes</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)}>
          <Plus className="h-4 w-4 mr-2" /> Create Coupon
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader><CardTitle>New Coupon</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-sm font-medium">Code *</label>
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="e.g. SAVE20" required />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Discount Type *</label>
                <select className="w-full px-3 py-2 border rounded-md" value={form.discountType} onChange={(e) => setForm({ ...form, discountType: e.target.value })}>
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount (IQD)</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Discount Value *</label>
                <Input type="number" value={form.discountValue} onChange={(e) => setForm({ ...form, discountValue: e.target.value })} placeholder={form.discountType === 'percentage' ? 'e.g. 20' : 'e.g. 50000'} required />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Max Uses (empty = unlimited)</label>
                <Input type="number" value={form.maxUses} onChange={(e) => setForm({ ...form, maxUses: e.target.value })} placeholder="e.g. 100" />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Specific Course (empty = all courses)</label>
                <select className="w-full px-3 py-2 border rounded-md" value={form.courseId} onChange={(e) => setForm({ ...form, courseId: e.target.value })}>
                  <option value="">All Courses</option>
                  {courses?.map((c: any) => <option key={c.id} value={c.id}>{c.titleEn}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Min Price (IQD)</label>
                <Input type="number" value={form.minPrice} onChange={(e) => setForm({ ...form, minPrice: e.target.value })} placeholder="0" />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Expires At</label>
                <Input type="date" value={form.expiresAt} onChange={(e) => setForm({ ...form, expiresAt: e.target.value })} />
              </div>
              <div className="flex items-end">
                <Button type="submit" disabled={createMutation.isPending} className="w-full">
                  {createMutation.isPending ? 'Creating...' : 'Create Coupon'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Coupons Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground">Loading...</div>
          ) : !coupons?.length ? (
            <div className="p-8 text-center text-muted-foreground">No coupons yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium">Code</th>
                    <th className="text-left p-3 font-medium">Discount</th>
                    <th className="text-left p-3 font-medium">Course</th>
                    <th className="text-left p-3 font-medium">Usage</th>
                    <th className="text-left p-3 font-medium">Expires</th>
                    <th className="text-left p-3 font-medium">Status</th>
                    <th className="text-left p-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {coupons.map((coupon: any) => (
                    <tr key={coupon.id} className={!coupon.isActive ? 'opacity-50' : ''}>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Tag className="h-4 w-4 text-primary" />
                          <span className="font-mono font-bold">{coupon.code}</span>
                        </div>
                      </td>
                      <td className="p-3">
                        {coupon.discountType === 'percentage' ? `${coupon.discountValue}%` : formatPrice(coupon.discountValue)}
                      </td>
                      <td className="p-3">{coupon.course?.titleEn || 'All courses'}</td>
                      <td className="p-3">{coupon.usedCount}{coupon.maxUses ? `/${coupon.maxUses}` : ''}</td>
                      <td className="p-3">
                        {coupon.expiresAt ? new Date(coupon.expiresAt).toLocaleDateString() : 'Never'}
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${coupon.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {coupon.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => toggleMutation.mutate({ id: coupon.id, isActive: !coupon.isActive })}
                            title={coupon.isActive ? 'Deactivate' : 'Activate'}
                          >
                            {coupon.isActive ? <ToggleRight className="h-5 w-5 text-green-600" /> : <ToggleLeft className="h-5 w-5 text-gray-400" />}
                          </button>
                          <button
                            onClick={() => { if (confirm('Delete this coupon?')) deleteMutation.mutate(coupon.id); }}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
